import { ThemeEvents, VariantUpdateEvent } from '@theme/events';
import { Component } from '@theme/component';

/**
 * @typedef {Object} ProductPriceRefs
 * @property {HTMLElement} priceContainer
 * @property {HTMLElement} [volumePricingNote]
 */

/**
 * A custom element that displays a product price.
 * This component listens for variant update events and updates the price display accordingly.
 * It handles price updates from two different sources:
 * 1. Variant picker (in quick add modal or product page)
 * 2. Swatches variant picker (in product cards)
 *
 * @extends {Component<ProductPriceRefs>}
 */
class ProductPrice extends Component {
  connectedCallback() {
    super.connectedCallback();
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.addEventListener(ThemeEvents.variantUpdate, this.updatePrice);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.removeEventListener(ThemeEvents.variantUpdate, this.updatePrice);
  }

  /**
   * Determines whether every visible option group has a selected value.
   * Shopify's section HTML can still consider `selected_variant` blank when
   * Horizon requests by `option_values`; the event resource is the reliable
   * signal that the chosen option set resolves to a variant.
   * @param {VariantUpdateEvent} event
   * @returns {boolean}
   */
  #hasCompleteVariantSelection(event) {
    if (!event.detail.resource) return false;

    const picker = event.target instanceof HTMLElement ? event.target.closest('variant-picker') : null;
    if (!picker) return true;

    const fieldsets = Array.from(picker.querySelectorAll('fieldset.variant-option'));
    const selects = Array.from(picker.querySelectorAll('select.variant-option__select'));

    const allFieldsetsSelected = fieldsets.every((fieldset) =>
      fieldset.querySelector('input[type="radio"]:checked')
    );
    const allSelectsSelected = selects.every((select) => select instanceof HTMLSelectElement && select.value);

    return fieldsets.length + selects.length > 0 && allFieldsetsSelected && allSelectsSelected;
  }

  /**
   * Updates the price and volume pricing note.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updatePrice = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.target instanceof HTMLElement && event.target.dataset.productId !== this.dataset.productId) {
      return;
    }

    const { priceContainer, volumePricingNote } = this.refs;
    // Find the new product-price element in the updated HTML
    const newProductPrice = event.detail.data.html.querySelector(
      `product-price[data-block-id="${this.dataset.blockId}"]`
    );
    if (!newProductPrice) return;

    // Update price container
    const newPrice = newProductPrice.querySelector('[ref="priceContainer"]');
    if (newPrice && priceContainer) {
      let replacementPrice = newPrice;

      if (
        newPrice instanceof HTMLElement &&
        newPrice.dataset.orchardHiddenPrice === 'true' &&
        this.#hasCompleteVariantSelection(event)
      ) {
        const selectedPriceTemplate = newProductPrice.querySelector('template[ref="selectedPriceTemplate"]');

        if (selectedPriceTemplate instanceof HTMLTemplateElement) {
          const selectedPrice = selectedPriceTemplate.content.querySelector('[ref="priceContainer"]');

          if (selectedPrice) {
            replacementPrice = selectedPrice;
          }
        }
      }

      priceContainer.replaceWith(replacementPrice);
    }

    // Update volume pricing note
    const newNote = newProductPrice.querySelector('[ref="volumePricingNote"]');

    if (!newNote) {
      volumePricingNote?.remove();
    } else if (!volumePricingNote) {
      // Use newPrice since priceContainer was just replaced and now points to the detached element
      newPrice?.insertAdjacentElement('afterend', /** @type {Element} */ (newNote.cloneNode(true)));
    } else {
      volumePricingNote.replaceWith(newNote);
    }
  };
}

if (!customElements.get('product-price')) {
  customElements.define('product-price', ProductPrice);
}
